/**
 * Operator copy for an invoice's QuickBooks push state — the stage-7
 * instrument (founder ruling 2026-08-27), rebuilt for contract item 1.
 *
 * THE GOVERNING LAW (same as qboCopyStatus.ts): Olive states what it
 * OBSERVED, never what it infers about QuickBooks' present state. The
 * vocabulary splits in two, and the split is the whole feature:
 *
 *   ABSENCE IS KNOWN — the request was refused before the wire, the
 *   connection never opened, or Intuit answered and declined. Olive
 *   watched nothing arrive. It may say "nothing was sent".
 *
 *   ABSENCE IS NOT KNOWN — the request left and the answer did not come
 *   back (timeout, a 502/504 from either host, an unclassified end).
 *   Olive says it does not know, that sending again is how it finds out,
 *   and what to do with a duplicate if one turns up. It never says the
 *   retry is safe: the idempotency key is an HMAC under a rotatable
 *   secret, so the duplicate guard is not a promise copy may make.
 *
 * Every ruled class lives in ONE map, keyed by its wire token, and each
 * entry carries its knowability ruling. The class registry is DERIVED
 * from that map (never hand-written), so copy cannot ship for a class
 * nobody has ruled on, and pushStatusCopy renders from the same map, so
 * the list cell and the per-invoice panel cannot drift apart.
 *
 * The parameterised families are handled by prefix below: a validation
 * fault (qbo_validation_fault_<code>) is Intuit answering and declining
 * (knowable); a transport fault (qbo_api_fault_<status>) is an answer
 * that is not a result (unknowable at every status); and the four
 * Fault.type families (qbo_system_fault, qbo_service_fault,
 * qbo_authentication_fault, qbo_authorization_fault, each with an
 * optional _<code>) carry their family's ruling from the map itself —
 * the auth pair refuses before processing, the system pair cannot know.
 */

import { contactSentence, type SupportContact } from './errorCopy';

// One data-tone attribute on one cell, so one union. The first five are
// the push vocabulary; the rest arrived with 0171's copy check, which
// renders into the same column for a voided invoice.
export type PushTone =
  | 'sent'
  | 'unsent'
  | 'blocked'
  | 'working'
  | 'mismatch'
  | 'none'
  | 'unknown'
  | 'urgent'
  | 'quiet';

export interface PushStatusCopy {
  /** The chip. Short enough for a table cell. */
  label: string;
  /** What to do, and where. Shown on the row or in a tooltip. */
  detail: string;
  tone: PushTone;
}

/** The one ruled chip label for every class whose delivery Olive cannot
 *  confirm (ruling 2026-08-29). One agreed phrase, not four shades of
 *  doubt, so an operator scanning a month can sort by it. */
export const DELIVERY_UNCONFIRMED_LABEL = 'Delivery unconfirmed';

/** The shared tail every unknowable class carries. Three clauses, each
 *  load-bearing: Olive does not know; sending again is how it finds out;
 *  here is the act, and the system it happens in, if a duplicate turns
 *  up. The word "safe" is forbidden here and everywhere else on this
 *  surface: the key that would make it true can be rotated out from
 *  under it. */
export const DELIVERY_UNKNOWN_SENTENCE =
  "It isn't clear whether it was sent, so send it again to check, and void any second copy that turns up in QuickBooks.";

/** F3: the remedy the per-invoice panel renders under already_pushed.
 *  A push lands under whichever customer the client was linked to AT
 *  SEND TIME; relinking the client and sending again answers
 *  already_pushed, because Olive's push is create-only and a re-send
 *  cannot move a document QuickBooks already holds. Without this
 *  sentence that flow dead-ends as plain success with no path forward.
 *  Panel copy only: the list cell's pushed detail stays untouched. */
export const ALREADY_PUSHED_REMEDY_SENTENCE =
  'This went to QuickBooks under the customer linked when it was sent. If that customer was wrong, void this invoice and issue it again.';

interface PushFailureCopyEntry {
  /** The chip in the list cell: what an operator reads without hovering. */
  label: string;
  /** The one sentence every surface renders for this class. */
  detail: string;
  /** May this class assert that nothing was sent? The contract table's
   *  ruling. A class missing from this map is UNKNOWABLE by default. */
  known: boolean;
  /** WHERE the fix lives, when it is not here. These classes' sentences
   *  all name an act on the billing surface and then "send again", so a
   *  surface that offers only Send offers the SECOND step and hides the
   *  first. A class without this field is one where sending IS the act
   *  (nothing was configured wrong; the wire failed, or it was never
   *  sent), and the retry keeps the primary weight. Per-class truth
   *  belongs in this table, never in a second list a consumer keeps. */
  remedy?: 'billing_settings';
}

/**
 * The contract's table, transcribed: every exact-match failure class the
 * copy module answers for, with its knowability ruling and its one
 * sentence. Adding a class here without deciding `known` does not
 * compile; adding copy anywhere else fails the registry test.
 *
 * `client_link_realm_mismatch` is filed KNOWN because the service
 * refuses it BEFORE the wire (qboPushService.ts, the link's company is
 * verified before anything is handed to a socket), the same reason as
 * the other pre-wire refusals.
 */
export const PUSH_FAILURE_COPY: Readonly<Record<string, PushFailureCopyEntry>> = {
  // Refused before the wire: nothing was ever handed to a socket.
  client_not_linked: {
    known: true,
    label: 'Client not matched',
    detail:
      "This client isn't linked to a QuickBooks customer, so nothing was sent. Add them in QuickBooks if needed, press Match clients to QuickBooks customers, then send again.",
    remedy: 'billing_settings',
  },
  client_link_realm_mismatch: {
    known: true,
    label: 'Matched to another company',
    detail:
      'This client is linked to a customer in a different QuickBooks company, so nothing was sent. Press Match clients to QuickBooks customers again, then send.',
    remedy: 'billing_settings',
  },
  band_unmapped: {
    known: true,
    label: 'Rate band not mapped',
    detail:
      'A line uses a rate band that has no QuickBooks product yet, so nothing was sent. Pick its product in Admin, Billing, QuickBooks, then send again.',
    remedy: 'billing_settings',
  },
  config_incomplete: {
    label: 'Billing setup incomplete',
    detail:
      "The QuickBooks settings aren't finished, so nothing was sent. Ask {contact_billing} to finish them, then send again.",
    known: true,
    remedy: 'billing_settings',
  },
  snapshot_unmappable: {
    known: true,
    label: 'Invoice cannot be mapped',
    detail:
      "Something on this invoice can't go to QuickBooks, so nothing was sent. Tell {contact_billing} which invoice it is, so they can send it.",
  },
  // [Sweep F6, 2026-08-31] The frozen snapshot could not be READ to
  // derive the push identity (the idempotency key's canonicalize
  // refusal) — a per-invoice data defect, refused before any row or
  // wire existed. Distinct from snapshot_unmappable: unmappable means
  // the document cannot be EXPRESSED in QuickBooks; uncanonical means
  // it could not be read at all.
  snapshot_uncanonical: {
    known: true,
    label: 'Invoice cannot be read',
    detail:
      "This invoice couldn't be read to send it to QuickBooks, so nothing was sent. Tell {contact_billing} which invoice it is, so they can send it.",
  },
  // TENANT-WIDE, not per-client: every invoice is blocked, not this one.
  item_map_realm_mismatch: {
    known: true,
    label: 'Items mapped to another company',
    detail:
      'The rate band products are set for a different QuickBooks company, so nothing was sent. Pick them again for this company in Admin, Billing, QuickBooks, then send again.',
    remedy: 'billing_settings',
  },
  // The connection never opened (ECONNREFUSED only). This copy is ruled
  // out of scope and carried unchanged: its assertion is true.
  qbo_api_unreachable: {
    known: true,
    label: 'QuickBooks unreachable',
    detail:
      "QuickBooks can't be reached, so nothing was sent. Send again in a few minutes, and if it fails again, tell {contact_billing}.",
  },
  // Intuit answered and declined.
  qbo_auth_rejected: {
    known: true,
    label: 'QuickBooks refused access',
    detail:
      'QuickBooks turned the connection away, so nothing was sent. Press Reconnect QuickBooks in Admin, Billing, QuickBooks, then send again.',
  },
  // ---- Intuit's own Fault.type families (F13) ----
  // The classifier (qboPushFacts.ts) emits these BY NAME from Intuit's
  // closed Fault.type set: the bare base token when the fault carried no
  // numeric code, base_<code> when it did. The coded variants resolve to
  // these entries by prefix below, so the ruling and the sentence ride
  // the FAMILY.
  //
  // AUTHENTICATION (HTTP 401: the OAuth bearer was invalid or expired)
  // and AUTHORIZATION (HTTP 403: the authenticated account lacks access
  // to the realm or operation) are ruled KNOWN for the same reason
  // qbo_auth_rejected is: Intuit's auth layer refuses the request BEFORE
  // the create is processed — a 401/403 fault body is a refusal at the
  // door, and nothing was accepted.
  qbo_authentication_fault: {
    known: true,
    label: 'QuickBooks refused access',
    detail:
      "QuickBooks didn't accept the sign-in, so nothing was sent. Press Reconnect QuickBooks in Admin, Billing, QuickBooks, then send again.",
  },
  qbo_authorization_fault: {
    known: true,
    label: 'QuickBooks refused permission',
    detail:
      "The connected QuickBooks account can't create invoices, so nothing was sent. Press Reconnect QuickBooks in Admin, Billing, QuickBooks, sign in with an account that can, then send again.",
  },

  // ---- the connection never opened, so no request was ever made ----
  // These five are the `access.reason` values qboPushService hands to
  // failed() when resolveAccess loses: the push returns BEFORE any HTTP
  // call to Intuit. Nothing was sent, and Olive knows it. Without an
  // entry here absenceIsKnown falls to its safe default of false and the
  // operator is told to go and check QuickBooks for a copy that cannot
  // exist.
  token_absent: {
    known: true,
    label: 'QuickBooks not connected',
    detail: "QuickBooks isn't connected, so nothing was sent. Connect it in Admin, Billing, QuickBooks, then send again.",
    remedy: 'billing_settings',
  },
  token_read_failed: {
    known: true,
    label: 'Connection unreadable',
    detail:
      'The QuickBooks connection needs renewing, so nothing was sent. Press Reconnect QuickBooks in Admin, Billing, QuickBooks, then send again.',
  },
  token_refresh_failed: {
    known: true,
    label: 'Connection expired',
    detail:
      'The QuickBooks connection needs renewing, so nothing was sent. Press Reconnect QuickBooks in Admin, Billing, QuickBooks, then send again.',
    remedy: 'billing_settings',
  },
  token_rotation_failed: {
    known: true,
    label: 'Connection expired',
    detail:
      'The QuickBooks connection needs renewing, so nothing was sent. Press Reconnect QuickBooks in Admin, Billing, QuickBooks, then send again.',
    remedy: 'billing_settings',
  },
  token_reconnect_required: {
    known: true,
    label: 'Reconnect QuickBooks',
    detail: 'The QuickBooks connection needs renewing, so nothing was sent. Press Reconnect QuickBooks in Admin, Billing, QuickBooks, then send again.',
    remedy: 'billing_settings',
  },

  // ---- the classes that cannot know ----
  qbo_api_timeout: {
    known: false,
    label: DELIVERY_UNCONFIRMED_LABEL,
    detail: `QuickBooks didn't answer in time. ${DELIVERY_UNKNOWN_SENTENCE}`,
  },
  qbo_push_unclassified: {
    known: false,
    label: DELIVERY_UNCONFIRMED_LABEL,
    detail: `The send ended without a clear answer. ${DELIVERY_UNKNOWN_SENTENCE}`,
  },
  // Browser-side. Olive's OWN host answered 502/504, and that host's
  // server may have completed the create before it died.
  push_request_failed: {
    known: false,
    label: DELIVERY_UNCONFIRMED_LABEL,
    detail: `The send didn't get an answer. ${DELIVERY_UNKNOWN_SENTENCE}`,
  },
  // SystemFault and ServiceFault (F13): unlike the auth families there
  // is no pre-empt argument. Intuit's OWN process (or a service behind
  // it) failed while holding the request, and the create may have
  // committed before the fault was raised — the exact shape the
  // delivery-unknown family exists for. `known` stays FALSE.
  qbo_system_fault: {
    known: false,
    label: DELIVERY_UNCONFIRMED_LABEL,
    detail: `QuickBooks had a problem on its side. ${DELIVERY_UNKNOWN_SENTENCE}`,
  },
  qbo_service_fault: {
    known: false,
    label: DELIVERY_UNCONFIRMED_LABEL,
    detail: `QuickBooks had a problem on its side. ${DELIVERY_UNKNOWN_SENTENCE}`,
  },
  // Server-side crash: an attempt stranded at 'pending' past the
  // in-flight window, settled by the next openQboPushAttempt (0172
  // companion) so the one-pending fence can never wedge an invoice. The
  // process died between the create leaving and the answer being
  // recorded, so the invoice may be sitting at Intuit right now —
  // `known` stays FALSE, and the sentence carries the shared tail.
  push_crash_orphaned: {
    known: false,
    label: DELIVERY_UNCONFIRMED_LABEL,
    detail: `The send was cut off before QuickBooks answered. ${DELIVERY_UNKNOWN_SENTENCE}`,
  },
  // [Sweep F6, 2026-08-31] SUCCESS-ADJACENT, total_mismatch's sibling:
  // the invoice IS in QuickBooks (the id is on the row) but the totals
  // oracle could not COMPUTE its comparison — one operand was
  // unreadable. Not a mismatch (nothing disagreed; nothing was
  // compared) and not a failure (the document is live at Intuit).
  // `known` is false because this class may never assert "nothing was
  // sent" — the opposite is true — and false is also the safe default
  // ruling for any surface that mistakes it for a failure class.
  total_unverifiable: {
    known: false,
    label: 'Total unverified',
    detail:
      "This reached QuickBooks, but the total there couldn't be read. Check the total in QuickBooks, and don't send it again or you'll get a duplicate.",
  },
};

// DERIVED, never transcribed: the registry IS the copy map's keys, so
// the two cannot disagree and a class cannot ship without a ruling.
export const PUSH_FAILURE_CLASSES = Object.keys(PUSH_FAILURE_COPY);

/** qbo_validation_fault_<code>: Intuit answered and declined. An
 *  observation, not an inference, whatever the code. */
const VALIDATION_FAULT_PREFIX = 'qbo_validation_fault_';

/** qbo_api_fault_<status>: the transport handed back a status instead of
 *  a result. The request left; what happened after is not Olive's to
 *  say, at ANY status. */
const API_FAULT_PREFIX = 'qbo_api_fault_';

/** The four Intuit Fault.type family BASES (F13). Each base is a real
 *  wire token in its own right (the classifier emits it when the fault
 *  carried no numeric code) and lives in PUSH_FAILURE_COPY as an exact
 *  entry; a coded variant (base_<code>) resolves here to the SAME entry,
 *  so the knowability ruling and the sentence ride the family. */
const FAULT_FAMILY_BASES = [
  'qbo_system_fault',
  'qbo_service_fault',
  'qbo_authentication_fault',
  'qbo_authorization_fault',
] as const;

/** The family base for a CODED variant, or null. A code is required,
 *  matching the validation-fault guard: a bare trailing underscore is a
 *  malformed token nobody emits, and it must not inherit a refusal
 *  sentence from the KNOWN families. */
function faultFamilyBase(reason: string): string | null {
  for (const base of FAULT_FAMILY_BASES) {
    if (reason.startsWith(`${base}_`) && reason.length > base.length + 1) return base;
  }
  return null;
}

const VALIDATION_DUPLICATE_NUMBER = 'qbo_validation_fault_6140';

const VALIDATION_DUPLICATE_COPY: PushFailureCopyEntry = {
  // Intuit answered and declined: absence is known, per the family's
  // ruling in this module's header.
  known: true,
  label: 'Number already used',
  detail:
    'QuickBooks already has an invoice with this number, so nothing was sent. Void this one and issue it again to get a new number.',
};

const VALIDATION_GENERIC_COPY: PushFailureCopyEntry = {
  known: true,
  label: 'QuickBooks declined it',
  detail:
    "QuickBooks turned this invoice down, so nothing was sent. Check its customer and lines and send it again, and if it's turned down again, tell {contact_billing}.",
};

// One sentence for the whole fault family: the status code is a wire
// fact for the log, and the operator act is identical for all of them.
const API_FAULT_COPY: PushFailureCopyEntry = {
  // An answer that is not a result: unknowable at every status.
  known: false,
  label: DELIVERY_UNCONFIRMED_LABEL,
  detail: `QuickBooks answered with an error. ${DELIVERY_UNKNOWN_SENTENCE}`,
};

/** True when this token belongs to the push-failure vocabulary this
 *  module answers for: a registered class or one of the two families. */
export function isPushFailureClass(reason: string): boolean {
  return (
    Object.prototype.hasOwnProperty.call(PUSH_FAILURE_COPY, reason) ||
    reason.startsWith(VALIDATION_FAULT_PREFIX) ||
    reason.startsWith(API_FAULT_PREFIX) ||
    faultFamilyBase(reason) !== null
  );
}

/**
 * THE PREDICATE: may this class assert that nothing was sent?
 *
 * The default falls to FALSE. A new class that quietly inherited
 * "nothing was sent" is exactly the failure this item exists to end, and
 * the safe default costs one honest sentence.
 */
export function absenceIsKnown(reason: string | null | undefined): boolean {
  if (reason == null || reason === '') return false;
  const entry = PUSH_FAILURE_COPY[reason];
  if (entry !== undefined) return entry.known;
  if (reason.startsWith(VALIDATION_FAULT_PREFIX) && reason.length > VALIDATION_FAULT_PREFIX.length) {
    return true;
  }
  // A coded Fault.type variant carries its family's ruling (F13): the
  // auth families refuse before processing at every code, the system
  // families cannot know at any.
  const familyBase = faultFamilyBase(reason);
  if (familyBase !== null) return PUSH_FAILURE_COPY[familyBase]!.known;
  return false;
}

function failureEntry(reason: string | null | undefined): PushFailureCopyEntry {
  if (reason != null && reason !== '') {
    const entry = PUSH_FAILURE_COPY[reason];
    if (entry !== undefined) return entry;
    // A code is required, matching absenceIsKnown: a bare prefix with no
    // code is a malformed token, and it must not inherit a sentence that
    // claims non-delivery while the predicate rules it unknowable.
    if (reason.startsWith(VALIDATION_FAULT_PREFIX) && reason.length > VALIDATION_FAULT_PREFIX.length) {
      return reason === VALIDATION_DUPLICATE_NUMBER
        ? VALIDATION_DUPLICATE_COPY
        : VALIDATION_GENERIC_COPY;
    }
    if (reason.startsWith(API_FAULT_PREFIX)) return API_FAULT_COPY;
    // A coded Fault.type variant renders its family's one sentence
    // (F13); the code is a wire fact for the log, never the screen.
    const familyBase = faultFamilyBase(reason);
    if (familyBase !== null) return PUSH_FAILURE_COPY[familyBase]!;
  }
  // A failure with no class, or a class nobody has ruled on, is the
  // purest case of not knowing. It never inherits a refusal sentence.
  return PUSH_FAILURE_COPY.qbo_push_unclassified;
}

/** The one sentence every surface renders for a failure class. Identity,
 *  not similarity: the list, the panel, and the batch bar all call this
 *  (directly or through qboReasonSentence), so one fact gets one string.
 *  A sentence that names who to tell carries `{contact_billing}`, filled
 *  here from the caller's support contact. */
export function pushFailureSentence(
  reason: string | null | undefined,
  contact: SupportContact,
): string {
  return contactSentence(failureEntry(reason).detail, contact);
}

/** WHERE this class's fix lives, when it is not on the invoice. Returns
 *  undefined for the classes where sending again IS the act, so a
 *  surface can offer the first step first and let the retry step back.
 *  Reads the same table as the sentence: a class cannot name an act in
 *  its copy and disagree with this about where that act happens. */
export function pushFailureRemedy(
  reason: string | null | undefined,
): PushFailureCopyEntry['remedy'] {
  return failureEntry(reason).remedy;
}

/**
 * The sync error alert's two groups (PR 4b), in the batch bar's rule:
 * 'not_sent' only where absenceIsKnown says Olive watched nothing
 * arrive, and 'no_answer' everywhere else, unclassified and unknown
 * classes included. One predicate, so the alert, the batch bar and the
 * list cell cannot sort one failure two ways.
 */
export type PushFailedKind = 'not_sent' | 'no_answer';

export function pushFailedKind(lastError: string | null | undefined): PushFailedKind {
  return absenceIsKnown(lastError) ? 'not_sent' : 'no_answer';
}

/**
 * Whether one invoice belongs in the sync error alert: the browser twin
 * of pushFailedSql (api/_shared/repos/invoices.ts). The latest attempt
 * failed, no attempt ever landed (a sticky QuickBooks id means it is in
 * QuickBooks, and a resend answers already_pushed), and the invoice is
 * not void.
 */
export function pushFailedAlertShows(
  latestStatus: string | null | undefined,
  stickyQboInvoiceId: string | null | undefined,
  invoiceStatus: string,
): boolean {
  return (
    latestStatus === 'failed' &&
    (stickyQboInvoiceId === null || stickyQboInvoiceId === undefined) &&
    invoiceStatus !== 'void'
  );
}

export function pushStatusCopy(
  status: string,
  lastError: string | null | undefined,
  qboInvoiceId: string | null,
  contact: SupportContact,
): PushStatusCopy {
  if (status === 'pushed') {
    // SENT, BUT WRONG. total_mismatch means the invoice really is at
    // Intuit — so it must never read as "not sent", or a retry would
    // create a SECOND invoice — but the total QuickBooks recorded does
    // not match Olive's frozen snapshot. Money is already out the door
    // with a number nobody reconciled. That earns the loudest tone on
    // this surface, louder than an unsent invoice, because an unsent one
    // has harmed nothing yet.
    if (lastError === 'total_mismatch') {
      return {
        label: qboInvoiceId ? `Total differs · ${qboInvoiceId}` : 'Total differs',
        detail:
          "This reached QuickBooks, but the total there doesn't match this invoice. Compare the two before the family gets it, and don't send it again or you'll get a duplicate.",
        tone: 'mismatch',
      };
    }
    // SENT, BUT UNCHECKED (sweep F6): the sibling state. The oracle
    // could not compute its comparison, so this is neither the green
    // row (nothing certified the total) nor the mismatch row (nothing
    // disagreed). Same tone and the same keep-the-id discipline; its
    // own label, so an operator can tell "compared and wrong" from
    // "never compared". The sentence comes from the one ruled map.
    if (lastError === 'total_unverifiable') {
      const entry = PUSH_FAILURE_COPY.total_unverifiable!;
      return {
        label: qboInvoiceId ? `${entry.label} · ${qboInvoiceId}` : entry.label,
        detail: contactSentence(entry.detail, contact),
        tone: 'mismatch',
      };
    }
    return {
      label: qboInvoiceId ? `In QuickBooks · ${qboInvoiceId}` : 'In QuickBooks',
      detail: 'Sent to QuickBooks. Add any billable expenses there, then send it to the family.',
      tone: 'sent',
    };
  }

  if (status === 'pending') {
    return { label: 'Sending', detail: 'A send is in progress.', tone: 'working' };
  }

  if (status === 'failed') {
    // The whole sentence comes from the class, through the same map the
    // registry derives from. A class with no ruling renders as
    // unclassified: "Delivery unconfirmed", never "Not sent" — the
    // request left Olive's process, and what happened after that is not
    // Olive's to report.
    const entry = failureEntry(lastError);
    return { label: entry.label, detail: contactSentence(entry.detail, contact), tone: 'blocked' };
  }

  // never_pushed — the most common case, and the one with no error to
  // find it by. This is the reason the column exists. "Not sent" is TRUE
  // here: an invoice nobody has sent has been observed unsent.
  return {
    label: 'Not sent',
    detail: 'This invoice has not been sent to QuickBooks yet.',
    tone: 'unsent',
  };
}

/** True when the row needs a human before the month can close.
 *
 *  NOT simply "not pushed": a pushed invoice whose total disagrees with
 *  the frozen snapshot is the most urgent row on the page, and an
 *  attention filter that skipped it would hide the one error that has
 *  already reached a family. A total nobody could COMPARE (sweep F6)
 *  needs the same eyes: nothing certified it either. */
export function needsAttention(status: string, lastError: string | null): boolean {
  return (
    status !== 'pushed' || lastError === 'total_mismatch' || lastError === 'total_unverifiable'
  );
}
